import React from 'react';
import styled from 'styled-components';
import { CampaignAnalytics, CampaignChannel } from '../../../models';
import { useTranslations } from '../../../store/translations/hooks';
import { TK } from '../../../store/translations/translationKeys';

const Strip = styled.div`
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(130px, 1fr));
    gap: var(--spacer-S);
`;

const tileBase = `
    padding: var(--spacer-S);
    border-radius: var(--spacer-XS);
`;

const Tile = styled.div`
    ${tileBase}
    border: var(--border-thin) solid var(--appLigthGrey);
    background: white;
`;

const TileHighlight = styled.div`
    ${tileBase}
    border: var(--border-thin) solid var(--appLigthGrey);
    border-left: var(--border-thick) solid var(--appBlue);
    background: var(--bg-hover-light);
`;

const TileLabel = styled.div`
    color: var(--text-muted);
    font-size: 0.75rem;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.04em;
`;

const TileValue = styled.div`
    color: var(--text-body);
    font-size: 1.4rem;
    font-weight: 800;
    margin-top: var(--spacer-XXS);
`;

const formatRate = (numerator: number, denominator: number): string => {
    if (denominator === 0) return '—';
    return `${Math.round((numerator / denominator) * 100)}%`;
};

interface CampaignMetricStripProps {
    analytics: CampaignAnalytics;
}

export const CampaignMetricStrip: React.FC<CampaignMetricStripProps> = ({ analytics }) => {
    const t = useTranslations();
    const isEmail = analytics.channel === CampaignChannel.Email;

    const successRate = formatRate(analytics.totalDelivered || analytics.totalSent, analytics.totalRecipients);

    return (
        <Strip>
            <Tile>
                <TileLabel>{t(TK.total)}</TileLabel>
                <TileValue>{analytics.totalRecipients}</TileValue>
            </Tile>
            <Tile>
                <TileLabel>{t(TK.sent)}</TileLabel>
                <TileValue>{analytics.totalSent}</TileValue>
            </Tile>
            <Tile>
                <TileLabel>{t(TK.delivered)}</TileLabel>
                <TileValue>{analytics.totalDelivered > 0 ? analytics.totalDelivered : '—'}</TileValue>
            </Tile>
            <Tile>
                <TileLabel>{t(TK.failed)}</TileLabel>
                <TileValue>{analytics.totalFailed}</TileValue>
            </Tile>
            <Tile>
                <TileLabel>{t(TK.pending)}</TileLabel>
                <TileValue>{analytics.totalPending}</TileValue>
            </Tile>
            {isEmail && (
                <>
                    <Tile>
                        <TileLabel>{t(TK.opened)}</TileLabel>
                        <TileValue>{analytics.totalOpened > 0 ? analytics.totalOpened : '—'}</TileValue>
                    </Tile>
                    <Tile>
                        <TileLabel>{t(TK.clicked)}</TileLabel>
                        <TileValue>{analytics.totalClicked > 0 ? analytics.totalClicked : '—'}</TileValue>
                    </Tile>
                </>
            )}
            {!isEmail && (
                <>
                    <Tile>
                        <TileLabel>{t(TK.campaignAnalyticsRead)}</TileLabel>
                        <TileValue>{analytics.totalRead > 0 ? analytics.totalRead : '—'}</TileValue>
                    </Tile>
                    {analytics.totalReplied != null && (
                        <Tile>
                            <TileLabel>{t(TK.campaignAnalyticsReplied)}</TileLabel>
                            <TileValue>{analytics.totalReplied > 0 ? analytics.totalReplied : '—'}</TileValue>
                        </Tile>
                    )}
                </>
            )}
            <TileHighlight>
                <TileLabel>{t(TK.campaignAnalyticsSuccessRate)}</TileLabel>
                <TileValue>{successRate}</TileValue>
            </TileHighlight>
        </Strip>
    );
};
